import { useEffect, useState } from 'react';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { formatDateUTC } from '../utils/formatDate';

import { useNavigate, useParams } from 'react-router-dom';
import Button from '../Components/Buttons';
import { ConnectionApiProvider, useConnectionApi } from '../Services/ConnectionApiProvider';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

const connectionTypeLabels = {
  NOTION: 'Notion',
  KEYCRM: 'CRM - KeyCRM',
  CREATIO: 'CRM - Creatio',
  JIRA: 'Jira',
  TRELLO: 'Trello',
  ZOHO: 'Zoho',
  ZENDESK: 'Zendesk',
  INTERCOM: 'Intercom',
  A2A: 'A2A',
  GOOGLE_DRIVE: 'Google Drive',
  GOOGLE_CALENDAR: 'Google Calendar',
  MCP: 'MCP',
  API: 'API',
};

function Connections({ canEdit }) {
  const { getConnection, deleteConnection } = useConnectionApi();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connections, setConnections] = useState([]);
  const [totalConnections, setTotalConnections] = useState(0);
  const navigate = useNavigate();

  const tableTitle = 'Connections';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: true },
    { name: 'Type', selector: (row) => connectionTypeLabels[row.type] || row.type, sortable: true },
    { name: 'Name', selector: (row) => row.name, sortable: true },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive'), sortable: true },
    { name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: true },
    { name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: true },
  ];

  const fetchConnections = async (skip, limit) => {
    try {
      setLoading(true);
      const response = await getConnection(skip, limit);
      setConnections(response.items);
      setTotalConnections(response.totalCount);
    } catch (error) {
      console.error('Error fetching connections:', error);
      setError('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections(persistedSkip, persistedPageSize);
  }, []);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/connections/${row.id}`);
  };

  const handleDelete = async (selectedId) => {
    if (!selectedId) return;
    showConfirmation(`Are you sure you want to delete the selected Connection?`, async () => {
      try {
        setLoading(true);
        await deleteConnection(selectedId);
        fetchConnections(persistedSkip, persistedPageSize);
        showAlert('success', 'Connection was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the connection!');
      }
    });
  };

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={connections}
                selectableRowsHighlight
                selectableRows
                progressPending={loading}
                paginationPerPage={persistedPageSize}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                paginationTotalRows={totalConnections}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onChangePage={fetchConnections}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/connections/new-connection`)
                  }
                >
                  Create New connection
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ConnectionsWrapper({ canEdit }) {
  return (
    <ConfirmationProvider>
      <ConnectionApiProvider>
        <Connections canEdit={canEdit} />
      </ConnectionApiProvider>
    </ConfirmationProvider>
  );
}

export default ConnectionsWrapper;
